import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePreviewAccount } from '@/hooks/account/usePreviewAccount';
import { useResolvedOwnerId } from '@/hooks/analytics/useResolvedOwnerId';
import { useAuth } from '@/hooks/use-auth';
import { useBrandKits } from '@/hooks/useBrandKits';
import { invalidateShopifyDataQueries } from '@/lib/analytics/invalidateShopifyDataQueries';
import { productService } from '@/services/data/ProductService';
import type {
  ProductWorkspaceScope,
  ShopifyConnection,
  ShopifyProduct,
} from '@/services/data/ProductService';
import type { PlatformConnectionState } from '@/services/socialAuth/connection-state';
import { showErrorToast, showInfoToast, showSuccessToast } from '@/utils/toast';

/** A fresh install is treated as "first sync in flight" for this long. */
export const FIRST_SYNC_WINDOW_MS = 2 * 60 * 1000;
const FIRST_SYNC_POLL_MS = 5 * 1000;

interface UseShopifyProductsOptions {
  searchQuery?: string;
  autoRefresh?: boolean;
  /**
   * Poll the products query every 5s while the catalog is empty AND the
   * connection is less than 2 minutes old (the DB trigger syncs in ~1s after
   * install). Stops on its own when rows arrive or the window passes.
   */
  pollFirstSync?: boolean;
}

interface UseShopifyProductsResult {
  products: ShopifyProduct[];
  connection: ShopifyConnection | null;
  isLoading: boolean;
  isConnectionLoading: boolean;
  error: string | null;
  hasConnection: boolean;
  connectionState: PlatformConnectionState;
  syncProducts: () => Promise<void>;
  searchProducts: (query: string) => void;
  saveProductToCache: (id: string | number) => Promise<unknown>;
  searchShopifyLive: (query: string) => Promise<any>;
  searchShopifyCollections: (query: string) => Promise<any>;
  isSyncing: boolean;
  isSaving: boolean;
  refetch: () => void;
  clearSearch: () => void;
}

/**
 * `hasConnection` means USABLE — we can call Shopify right now. It stays
 * `false` for a lapsed store so no caller starts talking to a dead token.
 *
 * `connectionState` is the honest three-way answer (`active` /`disconnected` /
 * `never_connected`) that UI must branch on instead: a lapsed store and a store
 * that was never linked are different situations with different fixes, and
 * collapsing them is what made the product picker tell a connected merchant to
 * "Connect Shopify". See `@/services/socialAuth/connection-state`.
 */
export function useShopifyConnection(): {
  connection: ShopifyConnection | null;
  isConnectionLoading: boolean;
  error: string | null;
  hasConnection: boolean;
  connectionState: PlatformConnectionState;
} {
  const { user, loading: authLoading } = useAuth();
  const { data: ownerId } = useResolvedOwnerId();
  const { activeKit, isActiveKitPending } = useBrandKits();
  const scope: ProductWorkspaceScope | null =
    ownerId && activeKit?.id ? { ownerId, brandKitId: activeKit.id } : null;

  const {
    data: connectionData,
    isLoading: isConnectionLoading,
    error: connectionError,
  } = useQuery({
    queryKey: ['shopify-connection', user?.id, scope?.ownerId, scope?.brandKitId],
    queryFn: async () => {
      const response = await productService.getStoreConnection(scope ?? undefined);
      if (response.error) throw response.error;
      return response.data;
    },
    // Gated on isActiveKitPending, not just !!scope: `activeKit` briefly
    // resolves to the account default before the per-user workspace pointer
    // loads (see useBrandKits' isActiveKitPending docs), and a moment of "no
    // scope yet" must not be read as "connection unknown, fall through" by a
    // caller that guards on isConnectionLoading alone.
    enabled: !!user?.id && !authLoading && !isActiveKitPending && !!scope,
    staleTime: 10 * 60 * 1000,
  });

  return {
    connection: connectionData || null,
    isConnectionLoading: isConnectionLoading || isActiveKitPending,
    error: connectionError?.message || null,
    hasConnection: connectionData?.state === 'active',
    connectionState: connectionData?.state ?? 'never_connected',
  };
}

export function useShopifyProducts(options?: UseShopifyProductsOptions): UseShopifyProductsResult {
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();
  const { isPreviewAccount, message: previewMessage } = usePreviewAccount();
  const [searchQuery, setSearchQuery] = useState(options?.searchQuery || '');

  // Sync external search query changes
  useEffect(() => {
    if (options?.searchQuery !== undefined) {
      setSearchQuery(options.searchQuery.trim());
    }
  }, [options?.searchQuery]);

  const { data: ownerId } = useResolvedOwnerId();
  const { activeKit } = useBrandKits();
  const scope: ProductWorkspaceScope | null = useMemo(
    () => (ownerId && activeKit?.id ? { ownerId, brandKitId: activeKit.id } : null),
    [ownerId, activeKit?.id],
  );

  const {
    connection: connectionData,
    isConnectionLoading,
    error: connectionError,
    hasConnection,
    connectionState,
  } = useShopifyConnection();

  const productScope: ProductWorkspaceScope | null = useMemo(() => {
    if (!scope?.ownerId || !scope.brandKitId) return null;
    return {
      ownerId: scope.ownerId,
      brandKitId: scope.brandKitId,
      ...(connectionData?.id ? { connectionId: connectionData.id } : {}),
    };
  }, [scope, connectionData?.id]);

  // Fetch products (with optional search)
  const {
    data: productsData,
    isLoading: isProductsLoading,
    error: productsError,
    refetch: refetchProducts,
  } = useQuery({
    queryKey: [
      'shopify-products',
      searchQuery,
      user?.id,
      productScope?.ownerId,
      productScope?.brandKitId,
      productScope?.connectionId,
    ],
    queryFn: async () => {
      const response = searchQuery
        ? await productService.searchProducts(searchQuery, productScope ?? undefined)
        : await productService.getProducts(productScope ?? undefined);
      if (response.error) throw response.error;
      return response.data;
    },
    enabled: !!connectionData && !!user?.id && !authLoading && !!scope, // Only fetch products if we have a connection, a resolved workspace, and are authenticated
    staleTime: 5 * 60 * 1000, // Consider products stale after 5 minutes
    refetchInterval: (query) => {
      if (options?.autoRefresh) return 60 * 1000; // Auto-refresh every minute if enabled
      if (!options?.pollFirstSync || connectionData?.state !== 'active') return false;
      if ((query.state.data?.length ?? 0) > 0) return false;
      const connectedMs = Date.parse(connectionData.connected_at);
      const inWindow =
        Number.isFinite(connectedMs) && Date.now() - connectedMs < FIRST_SYNC_WINDOW_MS;
      return inWindow ? FIRST_SYNC_POLL_MS : false;
    },
  });

  // Sync products mutation
  const syncMutation = useMutation({
    mutationFn: async () => {
      const response = await productService.syncProducts(productScope ?? undefined);
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['shopify-products'] });
      void invalidateShopifyDataQueries(queryClient);
      showSuccessToast(
        'Sync Complete',
        data?.success
          ? `Synced ${data.productsUpdated || 0} products and ${data.collectionsUpdated || 0} collections.`
          : data?.message || 'Products have been synchronized.',
      );
    },
    onError: (error) => {
      showErrorToast(
        'Sync Failed',
        error instanceof Error ? error.message : 'Failed to sync products from Shopify',
      );
    },
  });

  // Save product to cache mutation
  const saveProductMutation = useMutation({
    mutationFn: async (id: string | number) => {
      const response = await productService.saveProductToCache(id, productScope ?? undefined);
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['shopify-products'] });
    },
  });

  // Search function
  const searchProducts = useCallback((query: string) => {
    setSearchQuery(query.trim());
  }, []);

  // Clear search function
  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  // Refetch function
  const refetch = useCallback(() => {
    void refetchProducts();
    void queryClient.invalidateQueries({ queryKey: ['shopify-connection'] });
  }, [refetchProducts, queryClient]);

  const syncProducts = useCallback(async () => {
    // `sync-shopify-products` answers the preview 403 for a preview account; say it here
    // instead of surfacing a raw token error.
    if (isPreviewAccount) {
      showInfoToast(previewMessage);
      return;
    }
    await syncMutation.mutateAsync();
  }, [syncMutation, isPreviewAccount, previewMessage]);

  const saveProductToCache = useCallback(
    async (id: string | number) => {
      return await saveProductMutation.mutateAsync(id);
    },
    [saveProductMutation],
  );

  // Unmemoized before: every call to this hook returned a fresh function
  // reference here, which callers (ProductGrid) put in a useEffect dependency
  // array — that effect also unconditionally called setLiveProducts([]) with
  // a brand-new array each run, so the ever-changing reference plus the
  // never-bailing setState looped forever (blank/frozen /products page,
  // "Maximum update depth exceeded"). useCallback keeps this stable across
  // renders so the effect only re-runs when it actually should.
  const searchShopifyLive = useCallback(
    async (query: string) => {
      const response = await productService.searchShopifyLive(query, productScope ?? undefined);
      if (response.error) throw response.error;
      return response.data;
    },
    [productScope],
  );

  const searchShopifyCollections = useCallback(
    async (query: string) => {
      const response = await productService.searchShopifyCollections(
        query,
        productScope ?? undefined,
      );
      if (response.error) throw response.error;
      return response.data;
    },
    [productScope],
  );

  return {
    products: productsData || [],
    connection: connectionData || null,
    isLoading: isProductsLoading,
    isConnectionLoading,
    error: productsError?.message || connectionError || null,
    hasConnection,
    connectionState,
    syncProducts,
    searchProducts,
    saveProductToCache,
    searchShopifyLive,
    searchShopifyCollections,
    isSyncing: syncMutation.isPending,
    isSaving: saveProductMutation.isPending,
    refetch,
    clearSearch,
  };
}

interface UseShopifyProductResult {
  product: ShopifyProduct | null;
  isLoading: boolean;
  error: string | null;
}

export function useShopifyProduct(productId: string): UseShopifyProductResult {
  const { user, loading: authLoading } = useAuth();
  const { data: ownerId } = useResolvedOwnerId();
  const { activeKit, isActiveKitPending } = useBrandKits();
  const scope: ProductWorkspaceScope | null = useMemo(
    () => (ownerId && activeKit?.id ? { ownerId, brandKitId: activeKit.id } : null),
    [ownerId, activeKit?.id],
  );

  const {
    data: productData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['shopify-product', productId, user?.id, scope?.ownerId, scope?.brandKitId],
    queryFn: async () => {
      const response = await productService.getProductById(productId, scope ?? undefined);
      if (response.error) throw response.error;
      return response.data;
    },
    enabled: !!productId && !!user?.id && !authLoading && !isActiveKitPending && !!scope,
    staleTime: 10 * 60 * 1000, // Consider individual product stale after 10 minutes
  });

  return {
    product: productData || null,
    isLoading,
    error: error?.message || null,
  };
}
